/**
 * PrayerScheduleStrip — rolling banner showing 1 past + 5 upcoming prayers.
 *
 * Format:
 *   [Fajr 04:54  past] | [Dhuhr 13:32  NEXT  in 2h 45m] | Asr  17:10 | Maghrib 20:25 |
 *   Isha 21:48 | Fajr (tomorrow) 04:55 |
 *
 * - Past prayer: faded, leftmost chip.
 * - Next prayer: accent background + countdown "in Xh Ym".
 * - Upcoming: normal weight.
 * - "(tomorrow)" label in italic for cross-day entries.
 * - Horizontal-scroll on narrow screens; edge arrows indicate overflow.
 * - Re-renders every 60s via setInterval; pauses when tab hidden.
 * - RTL-aware (flex-direction inherited from [dir="rtl"] parent).
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { getLocationService } from '../../services/LocationService';

export class PrayerScheduleStrip {
  private root: HTMLElement | null = null;
  private deps: { ctx: AweniContext };
  private locUnsub: (() => void) | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private lastTickAt = 0;

  constructor(deps: { ctx: AweniContext }) {
    this.deps = deps;
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('section');
    root.className = 'aweni-prayer-strip';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label',
      this.deps.ctx.i18n.t('prayer_banner_label') || 'Prayer schedule');
    parent.appendChild(root);
    this.root = root;
    this.render();

    this.locUnsub = getLocationService().onChange(() => this.render());
    this.tick = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      // On stale wakeup (tab was hidden >5min) recompute fully
      if (Date.now() - this.lastTickAt > 6 * 60_000) this.render();
      else this.renderCountdown();
      this.lastTickAt = Date.now();
    }, 60_000);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible') this.render();
  };

  refresh(): void { this.render(); }

  unmount(): void {
    this.locUnsub?.();
    this.locUnsub = null;
    if (this.tick !== null) { clearInterval(this.tick); this.tick = null; }
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.root?.remove();
    this.root = null;
  }

  /** Full re-render — replaces DOM chips. */
  private render(): void {
    if (!this.root) return;
    this.lastTickAt = Date.now();
    const locale = this.deps.ctx.i18n.locale;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);

    const loc = getLocationService().getState();
    if (loc.lat === null || loc.lng === null) {
      this.root.style.display = 'none';
      return;
    }

    const now = new Date();
    const prev = prayerTimeService.getPreviousPrayer(now);
    const upcoming = prayerTimeService.getUpcomingBannerEntries(5, now);
    if (!prev && !upcoming.length) {
      this.root.style.display = 'none';
      return;
    }
    this.root.style.display = '';

    const fmt = (d: Date) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(d);
      } catch { return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
    };

    const countdown = (mins: number): string => {
      if (mins < 1) return t('prayer_in_moment') || 'now';
      if (mins < 60) return (t('prayer_in_min') || 'in {m}min').replace('{m}', String(mins));
      const h = Math.floor(mins / 60), m = mins % 60;
      return m === 0
        ? (t('prayer_in_h') || 'in {h}h').replace('{h}', String(h))
        : (t('prayer_in_hm') || 'in {h}h {m}min').replace('{h}', String(h)).replace('{m}', String(m));
    };

    const tomorrowLabel = t('prayer_tomorrow_short') || 'tomorrow';

    const chips: string[] = [];

    // 1 — previous (faded, no countdown)
    if (prev) {
      const name = prayerTimeService.getPrayerName(prev.name as never, locale as 'en');
      chips.push(`
        <span class="aweni-pb-chip is-past" role="listitem"
              aria-label="${escAttr(name)} ${escAttr(fmt(prev.time))} ${t('prayer_just_passed') || 'just passed'}">
          <span class="aweni-pb-name">${escHtml(name)}</span>
          <span class="aweni-pb-time">${escHtml(fmt(prev.time))}</span>
        </span>`);
    }

    // upcoming (index 0 = next = highlighted + countdown)
    upcoming.forEach((entry, i) => {
      const isNext = i === 0;
      const name = prayerTimeService.getPrayerName(entry.name as never, locale as 'en');
      const tomorrowBadge = entry.isTomorrow
        ? `<span class="aweni-pb-tomorrow" aria-label=", ${escAttr(tomorrowLabel)}">${escHtml(tomorrowLabel)}</span>`
        : '';
      const countdownBadge = isNext
        ? `<span class="aweni-pb-countdown" role="timer" aria-live="off">${escHtml(countdown(entry.minutesUntil))}</span>`
        : '';
      chips.push(`
        <span class="aweni-pb-chip${isNext ? ' is-next' : ''}" role="listitem"
              aria-current="${isNext ? 'true' : 'false'}"
              aria-label="${escAttr(name)} ${escAttr(fmt(entry.time))}${entry.isTomorrow ? ', ' + escAttr(tomorrowLabel) : ''}${isNext ? ', ' + escAttr(countdown(entry.minutesUntil)) : ''}">
          <span class="aweni-pb-name">${escHtml(name)}${tomorrowBadge}</span>
          <span class="aweni-pb-time">${escHtml(fmt(entry.time))}</span>
          ${countdownBadge}
        </span>`);
    });

    this.root.innerHTML = `
      <a href="#/prayer" class="aweni-pb-link" tabindex="0"
         aria-label="${escAttr(t('prayer_banner_label') || 'Prayer schedule')}">
        <span class="aweni-pb-row" role="list" aria-live="polite" aria-atomic="false">
          ${chips.join('')}
        </span>
      </a>`;
  }

  /** Light tick — only mutates countdown text without full DOM replacement. */
  private renderCountdown(): void {
    if (!this.root) return;
    const now = new Date();
    const upcoming = prayerTimeService.getUpcomingBannerEntries(5, now);
    if (!upcoming.length) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const countdown = (mins: number): string => {
      if (mins < 1) return t('prayer_in_moment') || 'now';
      if (mins < 60) return (t('prayer_in_min') || 'in {m}min').replace('{m}', String(mins));
      const h = Math.floor(mins / 60), m = mins % 60;
      return m === 0
        ? (t('prayer_in_h') || 'in {h}h').replace('{h}', String(h))
        : (t('prayer_in_hm') || 'in {h}h {m}min').replace('{h}', String(h)).replace('{m}', String(m));
    };
    const el = this.root.querySelector<HTMLElement>('.aweni-pb-countdown');
    if (el) el.textContent = countdown(upcoming[0].minutesUntil);

    // Flip "tomorrow" labels when midnight crosses
    const nextIsNext = this.root.querySelector('.aweni-pb-chip.is-next');
    if (nextIsNext && !upcoming[0].isTomorrow) {
      nextIsNext.querySelector('.aweni-pb-tomorrow')?.remove();
    }
  }

  private injectStyles(): void {
    if (document.getElementById('aweni-pb-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-pb-styles';
    style.textContent = `
      .aweni-prayer-strip { margin: 0 0 16px; }
      .aweni-pb-link {
        display: block;
        background: var(--card-bg, #fff);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 14px;
        padding: 10px 8px;
        text-decoration: none;
        color: inherit;
        overflow: hidden;
        transition: border-color var(--motion-duration-fast) var(--motion-easing-default);
      }
      .aweni-pb-link:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-pb-link:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 2px;
      }
      .dark .aweni-pb-link {
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
      }
      .aweni-pb-row {
        display: flex;
        gap: 4px;
        overflow-x: auto;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -ms-overflow-style: none;
        align-items: stretch;
      }
      .aweni-pb-row::-webkit-scrollbar { display: none; }
      .aweni-pb-chip {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        padding: 6px 10px;
        border-radius: 10px;
        flex: 0 0 auto;
        min-width: 64px;
        text-align: center;
        transition: background var(--motion-duration-fast) var(--motion-easing-default);
      }
      .aweni-pb-chip.is-past { opacity: 0.4; }
      .aweni-pb-chip.is-next {
        background: rgba(10,10,10,0.06);
        border: 1px solid rgba(10,10,10,0.12);
      }
      .dark .aweni-pb-chip.is-next {
        background: rgba(255,255,255,0.08);
        border-color: rgba(255,255,255,0.15);
      }
      .aweni-pb-name {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: var(--text-secondary, #6B7280);
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 3px;
      }
      .aweni-pb-chip.is-next .aweni-pb-name { color: var(--accent, #0A0A0A); }
      .dark .aweni-pb-chip.is-next .aweni-pb-name { color: var(--ln-accent, #9C8348); }
      .aweni-pb-time {
        font-size: 14px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--text-primary, #1E293B);
        white-space: nowrap;
        min-width: 42px;
      }
      .dark .aweni-pb-time { color: #F1F5F9; }
      .aweni-pb-chip.is-next .aweni-pb-time { color: var(--accent, #0A0A0A); }
      .dark .aweni-pb-chip.is-next .aweni-pb-time { color: var(--ln-accent, #9C8348); }
      .aweni-pb-countdown {
        font-size: 10px;
        font-weight: 500;
        color: var(--accent, #0A0A0A);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .dark .aweni-pb-countdown { color: var(--ln-accent, #9C8348); }
      .aweni-pb-tomorrow {
        font-style: italic;
        font-size: 9px;
        color: var(--text-secondary, #9CA3AF);
        font-weight: 400;
        text-transform: none;
        letter-spacing: 0;
      }
      @media (max-width: 380px) {
        .aweni-pb-chip { min-width: 54px; padding: 5px 7px; }
        .aweni-pb-time { font-size: 13px; }
      }
    `;
    document.head.appendChild(style);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
